import { LoadingPage } from 'cleveron-web-components';
import React, { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import { SC_CONNECTIONS_PATH } from '../../constants';
import Error404 from '../ErrorPage/Error404';
import ApmLocationInfoEdit from './Apm/ApmLocationInfo/InfoEdit';
import Task from './Maintenance/Task';

const Apm = lazy(() => import('./Apm'));
const ApmBySerialRedirect = lazy(() => import('./Apm/ApmBySerialRedirect'));
const ApmDatapoint = lazy(() => import('./ApmDatapoint'));
const ApmEdit = lazy(() => import('./Apm/ApmEdit'));
const ApmInstallation = lazy(() => import('./Apm/ApmInstallation'));
const Apms = lazy(() => import('./Apms'));
const ApmInvoicing = lazy(() => import('./ApmInvoicing'));
const ApmSeries = lazy(() => import('./ApmSeries'));
const Article = lazy(() => import('./SpareParts/ArticleDatabase/Article'));
const ArticlesImport = lazy(() => import('./SpareParts/ArticleDatabase/ArticlesImport'));
const Checklist = lazy(() => import('./Installation/Checklist'));
const Cluster = lazy(() => import('./Cluster'));
const ClusterEdit = lazy(() => import('./Cluster/ClusterEdit'));
const Clusters = lazy(() => import('./Clusters'));
const ClusterType = lazy(() => import('./ClusterType'));
const ClusterTypeConfig = lazy(() => import('./ClusterType/ClusterTypeConfig'));
const Companies = lazy(() => import('./Companies'));
const Company = lazy(() => import('./Company'));
const CustomerPortal = lazy(() => import('./CustomerPortal'));
const CustomerPortalRequest = lazy(() => import('./CustomerPortal/Issue'));
const CustomerPortalDetails = lazy(() => import('./CustomerPortal/IssueDetails'));
const Environments = lazy(() => import('./Environments'));
const Installation = lazy(() => import('./Installation'));
const Maintenance = lazy(() => import('./Maintenance'));
const Notifications = lazy(() => import('./Notifications'));
const Plan = lazy(() => import('./Maintenance/Plan'));
const ReleaseNoteEdit = lazy(() => import('./ReleaseNotes/ReleaseNote/ReleaseNoteEdit'));
const ReleaseNotes = lazy(() => import('./ReleaseNotes'));
const ReleaseNoteView = lazy(() => import('./ReleaseNotes/ReleaseNote/ReleaseNoteView'));
const Report = lazy(() => import('./Installation/Report'));
const Root = lazy(() => import('./Root'));
const Scenario = lazy(() => import('./CustomerPortal/Scenario'));
const Server = lazy(() => import('./Server'));
const ServerDatapoint = lazy(() => import('./ServerDatapoint'));
const Servers = lazy(() => import('./Servers'));
const ServerType = lazy(() => import('./ServerType'));
const SpareParts = lazy(() => import('./SpareParts'));
const ScConnections = lazy(() => import('./ScConnections'));
const SparePartOrder = lazy(() => import('./SpareParts/Orders/Order'));
const SparePartOrderEdit = lazy(() => import('./SpareParts/Orders/OrderEdit'));
const Statistics = lazy(() => import('./Statistics'));
const Tasks = lazy(() => import('./Maintenance/Tasks'));
const Ticket = lazy(() => import('./Ticket'));
const TicketEdit = lazy(() => import('./Ticket/Edit'));
const TicketsDashboard = lazy(() => import('./TicketsDashboard'));
const TicketsList = lazy(() => import('./TicketsList'));
const WorkOrder = lazy(() => import('./Maintenance/WorkOrder'));
const WorkOrderNew = lazy(() => import('./Maintenance/WorkOrderNew'));
const WorkOrders = lazy(() => import('./Maintenance/WorkOrders'));

// eslint-disable-next-line react/display-name
const Routes = React.memo(({ id }) => (
    <Suspense fallback={<LoadingPage id={`${id}Loading`} />}>
        <Switch>
            <Route
                exact
                path={['/tickets']}
                render={() => <TicketsDashboard id={`${id}TicketsDashboard`} />}
            />
            <Route
                exact
                path={['/tickets/tickets']}
                render={() => <TicketsList id={`${id}TicketsList`} />}
            />
            <Route
                exact
                path={['/tickets/tickets/:itemId']}
                render={() => <Ticket id={`${id}Ticket`} />}
            />
            <Route
                exact
                path={['/tickets/tickets/:itemId/edit', '/tickets/tickets/:itemId/edit/:viewId']}
                render={() => <TicketEdit id={`${id}TicketEdit`} />}
            />
            <Route
                exact
                path={['/apms', '/apms/:viewId']}
                render={({ match: { params } }) => <Apms id={`${id}Apms`} viewId={params.viewId} />}
            />
            <Route
                exact
                path="/apms/apms/ext"
                render={() => <ApmBySerialRedirect id={`${id}ApmBySerialRedirect`} />}
            />
            <Route
                exact
                path={['/apms/apms/new', '/apms/apms/:itemId/edit']}
                render={({ match: { params } }) => (
                    <ApmEdit id={`${id}ApmEdit`} itemId={Number(params.itemId)} />
                )}
            />
            <Route
                exact
                path={[
                    '/apms/apms/:apmId/location-info/new',
                    '/apms/apms/:apmId/location-info/:itemId',
                ]}
                render={({ match: { params } }) => (
                    <ApmLocationInfoEdit
                        id={`${id}ApmLocationInfoEdit`}
                        itemId={Number(params.itemId)}
                    />
                )}
            />
            <Route
                exact
                path="/apms/apms/:apmId/installation"
                render={({ match: { params } }) => (
                    <ApmInstallation apmId={params.apmId} id={`${id}ApmEdit`} />
                )}
            />
            <Route
                exact
                path={[
                    '/apms/apms/:itemId',
                    '/apms/apms/:apmId/:viewId',
                    '/apms/apms/:apmId/:viewId/:subViewId',
                    '/apms/apms/:apmId/:viewId/:subViewId/:serviceUi',
                ]}
                render={({ match: { params } }) => (
                    <Apm apmId={params.apmId || params.itemId} id={`${id}Apm`} />
                )}
            />
            <Route
                exact
                path={['/apms/series/:itemId', '/apms/series/:itemId/:viewId']}
                render={() => <ApmSeries id={`${id}ApmSeriesItem`} />}
            />
            <Route
                exact
                path="/apms/datapoints/:itemId"
                render={({ match: { params } }) => (
                    <ApmDatapoint id={`${id}ApmDatapoint`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/apms/invoicings/:itemId"
                render={({ match: { params } }) => (
                    <ApmInvoicing id={`${id}ApmInvoicing`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path={`/${SC_CONNECTIONS_PATH}`}
                render={() => <ScConnections id={`${id}ScConnections`} />}
            />
            <Route
                exact
                path={['/companies/:itemId', '/companies/:itemId/:viewId']}
                render={() => <Company id={`${id}Company`} />}
            />
            <Route path="/companies" render={() => <Companies id={`${id}Companies`} />} />
            <Route
                exact
                path="/customer/requests/new"
                render={() => <CustomerPortalRequest id={`${id}CustomerPortalRequest`} />}
            />
            <Route
                exact
                path="/customer/requests/:itemId"
                render={({ match: { params } }) => (
                    <CustomerPortalDetails
                        id={`${id}CustomerPortalDetails`}
                        itemId={params.itemId}
                    />
                )}
            />
            <Route
                exact
                path="/customer/scenarios/:itemId"
                render={({ match: { params } }) => (
                    <Scenario id={`${id}Scenario`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path={['/customer', '/customer/:viewId']}
                render={({ match: { params } }) => (
                    <CustomerPortal id={`${id}CustomerPortal`} viewId={params.viewId} />
                )}
            />
            <Route path="/environments" render={() => <Environments id={`${id}Environments`} />} />
            <Route
                exact
                path="/installation/checklists/:itemId"
                render={({ match: { params } }) => (
                    <Checklist id={`${id}InstallationChecklist`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/installation/reports/:itemId"
                render={({ match: { params } }) => (
                    <Report id={`${id}InstallationReport`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path={['/installation', '/installation/:viewId']}
                render={({ match: { params } }) => (
                    <Installation id={`${id}Installation`} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path="/maintenance/orders/new"
                render={() => <WorkOrderNew id={`${id}WorkOrderNew`} />}
            />
            <Route
                exact
                path="/maintenance/orders/:itemId"
                render={({ match: { params } }) => (
                    <WorkOrder id={`${id}WorkOrder`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/maintenance/tasks/:itemId"
                render={({ match: { params } }) => <Task id={`${id}Task`} itemId={params.itemId} />}
            />
            <Route
                exact
                path="/maintenance/plans/:itemId"
                render={({ match: { params } }) => <Plan id={`${id}Plan`} itemId={params.itemId} />}
            />
            <Route
                exact
                path={['/maintenance', '/maintenance/:viewId']}
                render={({ match: { params } }) => (
                    <Maintenance id={`${id}Maintenance`} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path="/notifications"
                render={() => <Notifications id={`${id}Notifications`} />}
            />
            <Route exact path="/statistics" render={() => <Statistics id={`${id}Statistics`} />} />
            <Route exact path="/maintenance/tasks" render={() => <Tasks id={`${id}Tasks`} />} />
            <Route
                exact
                path="/maintenance/orders"
                render={() => <WorkOrders id={`${id}WorkOrders`} />}
            />
            <Route
                exact
                path={['/servers', '/servers/:viewId']}
                render={({ match: { params } }) => (
                    <Servers id={`${id}Servers`} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path="/servers/datapoints/:itemId"
                render={({ match: { params } }) => (
                    <ServerDatapoint id={`${id}ServerDatapoint`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/servers/types/:itemId"
                render={({ match: { params } }) => (
                    <ServerType id={`${id}ServerType`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path={['/servers/servers/:itemId', '/servers/servers/:itemId/:viewId']}
                render={({ match: { params } }) => (
                    <Server id={`${id}Server`} itemId={params.itemId} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path={['/clusters', '/clusters/:viewId']}
                render={({ match: { params } }) => (
                    <Clusters id={`${id}Clusters`} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path={['/clusters/types/:itemId', '/clusters/types/:itemId/:viewId']}
                render={({ match: { params } }) =>
                    params.itemId === 'new' ? (
                        <ClusterTypeConfig id={`${id}Create`} />
                    ) : (
                        <ClusterType id={`${id}ClusterType`} itemId={Number(params.itemId)} />
                    )
                }
            />
            <Route
                exact
                path={['/clusters/clusters/new', '/clusters/clusters/:itemId/edit']}
                render={({ match: { params } }) => (
                    <ClusterEdit id={`${id}ClusterEdit`} itemId={Number(params.itemId)} />
                )}
            />
            <Route
                exact
                path={[
                    '/clusters/clusters/:itemId',
                    '/clusters/clusters/:itemId/:viewId',
                    '/clusters/clusters/:itemId/:viewId/:subItemId',
                ]}
                render={({ match: { params } }) => (
                    <Cluster
                        id={`${id}Cluster`}
                        itemId={params.itemId}
                        subItemId={params.subItemId}
                    />
                )}
            />
            <Route
                exact
                path={['/spare-parts/orders/:itemId/edit', '/spare-parts/orders/new']}
                render={({ match: { params } }) => (
                    <SparePartOrderEdit id={`${id}PartChangeOrder`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/spare-parts/orders/:itemId"
                render={({ match: { params } }) => (
                    <SparePartOrder id={`${id}SparePartOrder`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path="/spare-parts/articles/import"
                render={() => <ArticlesImport id={`${id}ArticlesImport`} />}
            />
            <Route
                exact
                path="/spare-parts/articles/:itemId"
                render={({ match: { params } }) => (
                    <Article id={`${id}Article`} itemId={params.itemId} />
                )}
            />
            <Route
                exact
                path={['/spare-parts', '/spare-parts/:viewId']}
                render={({ match: { params } }) => (
                    <SpareParts id={`${id}Inventory`} viewId={params.viewId} />
                )}
            />
            <Route
                exact
                path={['/release-notes/:viewId/new', '/release-notes/:viewId/:itemId/edit']}
                render={({ match: { params } }) => (
                    <ReleaseNoteEdit
                        id={`${id}ReleaseNoteEdit`}
                        itemId={params.itemId}
                        view={params.viewId}
                    />
                )}
            />
            <Route
                exact
                path={['/release-notes/:viewId/:itemId']}
                render={({ match: { params } }) => (
                    <ReleaseNoteView
                        id={`${id}ReleaseNoteView`}
                        itemId={params.itemId}
                        viewId={params.viewId}
                    />
                )}
            />
            <Route
                exact
                path={['/release-notes', '/release-notes/:viewId']}
                render={({ match: { params } }) => (
                    <ReleaseNotes
                        id={`${id}ReleaseNotesList`}
                        itemId={params.itemId}
                        viewId={params.viewId}
                    />
                )}
            />
            <Route exact path="/" render={() => <Root id={`${id}Root`} />} />
            <Route render={() => <Error404 id={`${id}Error404`} />} />
        </Switch>
    </Suspense>
));

export default Routes;
